"use client";

import { useMemo, useState } from "react";
import raw from "@/data/ktb.json";
import { run, FILL_LABEL, type FillModel, type Params, type Row, type Series } from "@/lib/engine";
import { fmtEok, fmtNum, fmtPct, fmtPrice, fmtRatio } from "@/lib/format";
import { BacktestChart } from "@/components/backtest-chart";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

type Product = "ktb3" | "ktb10";
const DATA = raw as unknown as { meta: { source: string; built: string } } & Record<Product, Series>;
const PRODUCT_LABEL: Record<Product, string> = { ktb3: "3년 국채선물", ktb10: "10년 국채선물" };
// 종전 기본 주문 규모를 유지하되 장중 간격 가정 없이 일일 수량으로 지정
const DEFAULT_LOTS: Record<Product, number> = { ktb3: 39, ktb10: 13 };

const OPTIONS = {
  strategy: { ma: "이동평균선 비교", prevClose: "전일 종가 대비 매매" },
  product: { ktb3: "3년 국채선물 (수정연결)", ktb10: "10년 국채선물 (수정연결)" },
  timing: { prev: "전일 확정 → 다음 거래일 체결", open: "당일 시가 확인 → 당일 종가 체결" },
  buyRule: { open: "시가 > 5일선", high: "고가 > 5일선", low: "저가 > 5일선", close: "종가 > 5일선" },
  sellRule: { open: "시가 < 5일선", high: "고가 < 5일선", low: "저가 < 5일선", close: "종가 < 5일선" },
  mode: { ls: "롱·숏 모두", long: "롱 온리 (매도는 청산까지만)" },
  capDays: { "1": "1일치", "2": "2일치", "3": "3일치", "5": "5일치", "10": "10일치", "0": "무제한" },
  tpPriority: { ma: "매매 조건 우선", tp: "이격도 우선" },
} as const;

const FILL_OPTIONS: Record<FillModel, string> = {
  open: "시가 일괄", close: "종가 일괄", openClose: "시가·종가 분할",
};
const CLOSE_ONLY = { close: FILL_OPTIONS.close };
type UIState = Params & { product: Product; strategy: "ma" | "prevClose"; buyFill: FillModel; sellFill: FillModel };
const DEFAULT_STATE: UIState = {
  product: "ktb3", strategy: "ma", maLen: 5, dailyLots: 39, timing: "prev", buyRule: "close", sellRule: "high",
  mode: "ls", capDays: 5, tp: false, tpPriority: "ma", buyFill: "open", sellFill: "open",
  slipTicks: 0, feePerLot: 0, start: "2007-01-01", end: DATA.ktb3.d.at(-1)!,
};

export function Dashboard() {
  const [s, setS] = useState<UIState>(DEFAULT_STATE);
  const set = <K extends keyof UIState>(k: K, v: UIState[K]) => setS((o) => ({ ...o, [k]: v }));
  const D = DATA[s.product];
  const res = useMemo(() => run(D, s), [D, s]);
  const st = res.stats;
  const inconsistentDays = useMemo(() => D.d.filter((_, i) => D.h[i] < Math.max(D.o[i], D.c[i]) || D.l[i] > Math.min(D.o[i], D.c[i])).length, [D]);
  const atOpen = s.timing === "open";
  const thisYear = `${new Date().getFullYear()}-01-01`;
  const fillItems = atOpen ? CLOSE_ONLY : FILL_OPTIONS;
  const preset = (fill: FillModel) => setS((o) => ({ ...o, strategy: "ma", timing: "prev", buyRule: "low", sellRule: "low", buyFill: fill, sellFill: fill }));

  return (
    <main className="mx-auto flex w-full max-w-[1400px] flex-col gap-5 px-4 py-6 sm:px-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium tracking-[0.12em] text-muted-foreground uppercase">FITS · CM2 백테스트</p>
          <h1 className="text-2xl font-semibold tracking-tight text-balance">국채선물 조건·체결 백테스트</h1>
          <p className="max-w-[70ch] text-sm text-muted-foreground">
            신호가 확정된 뒤의 시가·종가에 체결합니다. 전일 OHLC 또는 당일 시가로 조건을 판단합니다.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5 text-xs">
          <Badge variant="outline">{DATA.meta.source}</Badge>
          <Badge variant="outline">{D.d[0]} ~ {D.d[D.d.length - 1]}</Badge>
          <Badge variant="outline">1pt = 100만원/계약</Badge>
          {inconsistentDays > 0 && <Badge variant="outline" title="원본 시가·종가가 고가·저가 범위를 벗어난 일봉입니다. 가정·메모 탭을 확인하세요.">원본 OHLC 범위 불일치 {inconsistentDays}일</Badge>}
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-2" aria-label="전략 빠른 설정">
        <span className="text-xs text-muted-foreground">빠른 설정</span>
        <Button size="sm" variant="outline" onClick={() => preset("open")}>저가 기준 · 시가</Button>
        <Button size="sm" variant="outline" onClick={() => preset("close")}>저가 기준 · 종가</Button>
        <Button size="sm" variant="outline" onClick={() => preset("openClose")}>저가 기준 · 시가+종가</Button>
        <Button size="sm" variant="outline" onClick={() => setS((o) => ({ ...o, strategy: "prevClose", timing: "prev", buyFill: "open", sellFill: "open", tp: false }))}>종가 비교 · 다음 시가</Button>
        <Button size="sm" variant="outline" onClick={() => setS((o) => ({ ...o, strategy: "prevClose", timing: "open", buyFill: "close", sellFill: "close", tp: false }))}>시가 비교 · 당일 종가</Button>
        <Button size="sm" variant="ghost" onClick={() => setS((o) => ({ ...DEFAULT_STATE, product: o.product, dailyLots: DEFAULT_LOTS[o.product], start: o.start, end: o.end }))}>기본 설정</Button>
      </div>

      <Card size="sm">
        <CardContent className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          <Field id="product" label="상품" value={s.product} items={OPTIONS.product}
            onChange={(v) => setS((o) => ({ ...o, product: v as Product, dailyLots: DEFAULT_LOTS[v as Product] }))} />
          <Field id="strategy" label="전략" value={s.strategy} items={OPTIONS.strategy}
            onChange={(v) => set("strategy", v as UIState["strategy"])} />
          <NumField id="dailyLots" label="하루 최대 주문 수량 (계약)" value={s.dailyLots} min={1} step={1}
            onChange={(v) => set("dailyLots", Math.max(1, Math.floor(v)))} />
          <Field id="timing" label="신호 관측 → 체결 시점" value={s.timing} items={OPTIONS.timing}
            onChange={(v) => setS((o) => ({ ...o, timing: v as Params["timing"],
              buyFill: v === "open" ? "close" : o.buyFill,
              sellFill: v === "open" ? "close" : o.sellFill }))} />
          <Field id="buyFill" label="매수 체결" value={s.buyFill} items={fillItems}
            onChange={(v) => set("buyFill", v as FillModel)} />
          <Field id="sellFill" label="매도 체결" value={s.sellFill} items={fillItems}
            onChange={(v) => set("sellFill", v as FillModel)} />
          {s.strategy === "ma" && <>
            <Field id="buyRule" label={atOpen ? "매수 조건" : "전일 매수 조건"} value={atOpen ? "open" : s.buyRule}
              items={atOpen ? { open: "당일 시가 > 전일 5일선" } : OPTIONS.buyRule} disabled={atOpen}
              onChange={(v) => set("buyRule", v as Params["buyRule"])} />
            <Field id="sellRule" label={atOpen ? "매도 조건" : "전일 매도 조건"} value={atOpen ? "open" : s.sellRule}
              items={atOpen ? { open: "당일 시가 < 전일 5일선" } : OPTIONS.sellRule} disabled={atOpen}
              onChange={(v) => set("sellRule", v as Params["sellRule"])} />
          </>}
          <Field id="mode" label="방향" value={s.mode} items={OPTIONS.mode}
            onChange={(v) => set("mode", v as Params["mode"])} />
          <Field id="capDays" label={`최대 보유 (일일 ${res.lotsPerDay}계약 기준)`} value={String(s.capDays)} items={OPTIONS.capDays}
            onChange={(v) => set("capDays", Number(v))} />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tp" className="text-xs text-muted-foreground">이격도 변곡 익실현</Label>
            <div className="flex h-8 items-center gap-2">
              <Switch id="tp" checked={s.tp} onCheckedChange={(v) => set("tp", v)} />
              <span className="text-sm">{s.tp ? "사용" : "사용 안 함"}</span>
            </div>
          </div>
          <Field id="tpPriority" label="익실현 충돌 시" value={s.tpPriority} items={OPTIONS.tpPriority} disabled={!s.tp}
            onChange={(v) => set("tpPriority", v as Params["tpPriority"])} />
          <NumField id="slip" label="슬리피지 (틱/계약)" value={s.slipTicks} step={0.5} onChange={(v) => set("slipTicks", v)} />
          <NumField id="fee" label="수수료 (원/계약)" value={s.feePerLot} step={100} onChange={(v) => set("feePerLot", v)} />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="start" className="text-xs text-muted-foreground">시작일</Label>
            <Input id="start" type="date" value={s.start} min={D.d[0]} max={D.d[D.d.length - 1]}
              onChange={(e) => e.target.value && set("start", e.target.value)} className="h-8" />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="end" className="text-xs text-muted-foreground">종료일</Label>
            <Input id="end" type="date" value={s.end} min={D.d[0]} max={D.d[D.d.length - 1]}
              onChange={(e) => e.target.value && set("end", e.target.value)} className="h-8" />
          </div>
          <div className="flex items-end gap-1.5">
            <Button size="sm" variant="outline" onClick={() => setS((o) => ({ ...o, start: thisYear, end: D.d.at(-1)! }))}>올해</Button>
            <Button size="sm" variant="outline" onClick={() => setS((o) => ({ ...o, start: D.d[0], end: D.d.at(-1)! }))}>전체 기간</Button>
          </div>
        </CardContent>
      </Card>

      <div className="rounded-lg border px-3 py-2 text-sm leading-relaxed text-muted-foreground">
        <b className="text-foreground">하루 매수·매도 합계 최대 {res.lotsPerDay}계약</b> · 매수 {FILL_OPTIONS[s.buyFill]} / 매도 {FILL_OPTIONS[s.sellFill]}.
        {" "}시가·종가 일괄은 하루 수량을 한 번에 주문하며, 시가·종가 분할은 시가 {Math.floor(res.lotsPerDay / 2)}계약 + 종가 {Math.ceil(res.lotsPerDay / 2)}계약입니다. 미체결 수량은 다음 시점으로 이월하지 않습니다.
        <p className="mt-1"><b className="text-foreground">관측 → 주문 순서:</b> {atOpen ? "당일 시가를 관측해 신호를 확정하고, 당일 종가 주문을 미리 결정합니다. 장 마감 때 신호를 다시 판단하지 않습니다." : "전일 장 마감까지 확정된 정보로 주문을 결정한 뒤, 다음 거래일의 선택한 시가·종가에 체결합니다."}</p>
        {s.strategy === "prevClose" ? <p className="mt-1">{atOpen ? "당일 시가 < 전일 종가 → 매수 / 당일 시가 > 전일 종가 → 매도." : "전일 종가 < 전전 거래일 종가 → 매수 / 전일 종가 > 전전 거래일 종가 → 매도."} 같으면 관망합니다.</p>
          : <p className="mt-1">{atOpen ? "당일 시가와 전일 확정 5일선을 비교합니다." : "전일에 선택한 가격과 전일 확정 5일선을 비교합니다. 매수·매도 조건이 동시에 충족되면 관망합니다."} 5일선 자체는 종가의 단순이평입니다.</p>}
        <p className="mt-1">일봉에서 고가·저가의 발생 시각은 알 수 없습니다. 장중 경로·10분 분할·VWAP 체결은 제공하지 않습니다. 체결은 사전에 정한 주문이 시가·종가에 체결된다는 가정이며, 비용은 입력한 슬리피지·수수료로 반영합니다.</p>
      </div>

      {!st && <p role="status" className="rounded-lg border p-4 text-sm">선택한 기간에 계산 가능한 데이터가 없습니다. 시작일·종료일과 데이터 범위를 확인하세요.</p>}

      {st && (
        <section className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <Kpi label="누적 손익" value={fmtEok(st.total)} tone={st.total} sub={`${st.from} ~ ${st.to}`} />
          <Kpi label="연평균 손익" value={fmtEok(st.perYear, 2)} tone={st.perYear} sub={`${st.years.toFixed(1)}년`} />
          <Kpi label="최대 낙폭 (MDD)" value={fmtEok(st.mdd)} tone={-1} sub="누적손익 고점 대비" />
          <Kpi label="최장 언더워터" value={`${fmtNum(st.uwMaxDays)}일`}
            sub={st.uwMaxFrom ? `${st.uwMaxFrom} ~ ${st.uwMaxTo ?? "미회복"}` : "–"} />
          <Kpi label="현재 언더워터" value={st.uwNowDays ? `${fmtNum(st.uwNowDays)}일` : "고점"}
            tone={st.uwNowDays ? -1 : undefined} sub={`전체 기간의 ${fmtPct(st.uwShare, 0)}가 고점 아래`} />
          <Kpi label="연손익 / MDD" value={fmtRatio(st.calmar)} sub="Calmar 유사" />
          <Kpi label="샤프 (일간, 연환산)" value={fmtRatio(st.sharpe)} sub="일손익 평균/표준편차 × √252" />
          <Kpi label="일간 승률" value={fmtPct(st.hit)} sub="포지션 보유일 기준" />
          <Kpi label="평균 보유" value={`${fmtNum(st.avgAbsPos)}계약`} sub={`최대 +${fmtNum(st.maxLong)} / ${fmtNum(st.maxShort)}`} />
          <Kpi label="매매일수" value={`${st.buyDays} / ${st.sellDays}${s.tp ? ` / ${st.tpDays}` : ""}`}
            sub={`매수 / 매도${s.tp ? " / 익실현" : ""} (중복 가능) · 총 ${fmtNum(st.lots)}계약`} />
        </section>
      )}

      <Tabs defaultValue="chart">
        <TabsList>
          <TabsTrigger value="chart">차트 · 타점</TabsTrigger>
          <TabsTrigger value="year">연도별</TabsTrigger>
          <TabsTrigger value="trades">매매 내역</TabsTrigger>
          <TabsTrigger value="compare">민감도 비교</TabsTrigger>
          <TabsTrigger value="notes">가정 · 메모</TabsTrigger>
        </TabsList>

        <TabsContent value="chart" className="pt-3">
          <Card size="sm">
            <CardContent>
              {st && <BacktestChart rows={res.rows} maLen={s.maLen} lotsPerDay={res.lotsPerDay} showTurns={s.tp}
                uw={{ from: st.uwMaxFrom, to: st.uwMaxTo }} />}
              <p className="mt-3 text-xs text-muted-foreground">
                <span className="text-[var(--bt-up)]">▲B</span> 매수 ·{" "}
                <span className="text-[var(--bt-down)]">▼S</span> 매도 ·{" "}
                <span className="text-[var(--bt-tp)]">●TP</span> 이격도 익실현 · ■롤 월물 교체일 · 하루 양방향 매매는 양쪽 화살표로 표시합니다.
                차트를 드래그·휠로 확대하고, 마우스를 올리면 그날 신호와 체결 내역이 위에 표시됩니다.
                이평선·이격도는 당일 마감 후 확정값을 그린 것이며, 주문 당시 판단에 사용한 값은 별도의 신호일·기준일 표시를 확인하세요.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="year" className="pt-3">
          <YearTable years={res.years} tp={s.tp} />
        </TabsContent>

        <TabsContent value="trades" className="pt-3">
          <TradeTable rows={res.rows} maLen={s.maLen} strategy={s.strategy} />
        </TabsContent>

        <TabsContent value="compare" className="pt-3">
          <Compare base={s} />
        </TabsContent>

        <TabsContent value="notes" className="pt-3">
          <Notes />
        </TabsContent>
      </Tabs>
    </main>
  );
}

function Field({ id, label, value, items, onChange, disabled }: {
  id: string; label: string; value: string; items: Record<string, string>;
  onChange: (v: string) => void; disabled?: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
      <Select items={items} value={value} disabled={disabled} onValueChange={(v) => v != null && onChange(String(v))}>
        <SelectTrigger id={id} className="w-full min-w-0"><SelectValue /></SelectTrigger>
        <SelectContent>
          {Object.entries(items).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function NumField({ id, label, value, step, min = 0, onChange }: {
  id: string; label: string; value: number; step: number; min?: number; onChange: (v: number) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-xs text-muted-foreground">{label}</Label>
      <Input id={id} type="number" min={min} step={step} value={value} className="h-8 font-mono tabular-nums"
        onChange={(e) => onChange(Math.max(min, Number(e.target.value) || min))} />
    </div>
  );
}

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: number }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border bg-card px-3 py-2.5">
      <span className="text-[11px] tracking-wide text-muted-foreground">{label}</span>
      <span className={cn("font-mono text-lg font-semibold tabular-nums",
        tone != null && tone > 0 && "text-[var(--bt-up)]", tone != null && tone < 0 && "text-[var(--bt-down)]")}>{value}</span>
      {sub && <span className="truncate text-[11px] text-muted-foreground" title={sub}>{sub}</span>}
    </div>
  );
}

const signed = (v: number) => cn("text-right font-mono tabular-nums", v > 0 && "text-[var(--bt-up)]", v < 0 && "text-[var(--bt-down)]");

function YearTable({ years, tp }: { years: ReturnType<typeof run>["years"]; tp: boolean }) {
  return (
    <Card size="sm">
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>연도</TableHead>
              <TableHead className="text-right">손익</TableHead>
              <TableHead className="text-right">연중 MDD</TableHead>
              <TableHead className="text-right">가격 변동 (pt)</TableHead>
              <TableHead className="text-right">매수일</TableHead>
              <TableHead className="text-right">매도일</TableHead>
              {tp && <TableHead className="text-right">익실현일</TableHead>}
              <TableHead className="text-right">최대 보유</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {years.map((y) => (
              <TableRow key={y.y}>
                <TableCell className="font-medium">{y.y}</TableCell>
                <TableCell className={signed(y.pnl)}>{fmtEok(y.pnl, 2)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums text-muted-foreground">{fmtEok(y.mdd, 2)}</TableCell>
                <TableCell className={signed(y.move)}>{y.move > 0 ? "+" : ""}{y.move.toFixed(2)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{y.buyDays}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{y.sellDays}</TableCell>
                {tp && <TableCell className="text-right font-mono tabular-nums">{y.tpDays}</TableCell>}
                <TableCell className="text-right font-mono tabular-nums">{fmtNum(y.maxAbs)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function TradeTable({ rows, maLen, strategy }: { rows: Row[]; maLen: number; strategy: UIState["strategy"] }) {
  const [onlyTrades, setOnlyTrades] = useState(true);
  const [limit, setLimit] = useState(60);
  const list = useMemo(() => {
    const src = onlyTrades ? rows.filter((r) => r.action) : rows;
    return src.slice(-limit).reverse();
  }, [rows, onlyTrades, limit]);
  return (
    <Card size="sm">
      <CardHeader>
        <CardTitle>최근 매매 내역</CardTitle>
        <CardDescription>가격은 수정연결 기준. 체결 내역을 펼치면 판단 가격·기준값을 소수점 6자리까지 확인할 수 있습니다. 하루 양방향 거래의 순수량이 0이어도 거래량과 비용은 계산합니다.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-4 text-sm">
          <label className="flex items-center gap-2" htmlFor="onlyTrades">
            <Switch id="onlyTrades" checked={onlyTrades} onCheckedChange={setOnlyTrades} /> 매매한 날만
          </label>
          <label className="flex items-center gap-2" htmlFor="limit">
            표시
            <Input id="limit" type="number" min={10} step={20} value={limit} className="h-8 w-20 font-mono"
              onChange={(e) => setLimit(Math.max(10, Number(e.target.value) || 60))} />
            행
          </label>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>일자</TableHead>
                <TableHead className="text-right">종가</TableHead>
                <TableHead className="text-right">MA{maLen}</TableHead>
                <TableHead className="text-right">이격도</TableHead>
                <TableHead>판단 기준</TableHead>
                <TableHead>구분</TableHead>
                <TableHead className="text-right">매수 / 매도 (순수량)</TableHead>
                <TableHead>평균가 · 체결 상세</TableHead>
                <TableHead className="text-right">보유</TableHead>
                <TableHead className="text-right">일손익</TableHead>
                <TableHead className="text-right">누적</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map((r) => (
                <TableRow key={r.d}>
                  <TableCell className="font-mono tabular-nums">{r.d}{r.roll ? <Badge variant="outline" className="ml-1.5">롤</Badge> : null}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{fmtPrice(r.c)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-muted-foreground">{r.ma == null ? "–" : fmtPrice(r.ma)}</TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-muted-foreground">{r.disp?.toFixed(2) ?? "–"}</TableCell>
                  <TableCell className="text-xs">
                    <div>{r.signalDate} {r.signalPhase === "open" ? "시가 확인" : "장 마감 확정"}</div>
                    <div className="text-muted-foreground">{r.referenceDate} {strategy === "prevClose" ? "종가" : "MA"} {r.signalRef == null ? "–" : fmtPrice(r.signalRef, 6)}</div>
                    <div className="text-muted-foreground">매수 판단 {fmtPrice(r.signalBuy, 6)} / 매도 판단 {fmtPrice(r.signalSell, 6)}</div>
                  </TableCell>
                  <TableCell><ActionBadge r={r} /></TableCell>
                  <TableCell className="text-right font-mono tabular-nums">{r.buys} / {r.sells} <span className={signed(r.q)}>({r.q > 0 ? "+" : ""}{r.q})</span></TableCell>
                  <TableCell><ExecutionDetails row={r} strategy={strategy} /></TableCell>
                  <TableCell className={signed(r.pos)}>{fmtNum(r.pos)}</TableCell>
                  <TableCell className={signed(r.pnl)}>{fmtEok(r.pnl, 2)}</TableCell>
                  <TableCell className={signed(r.cum)}>{fmtEok(r.cum, 1)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}

function ExecutionDetails({ row, strategy }: { row: Row; strategy: UIState["strategy"] }) {
  if (!row.executions.length) return <span className="text-muted-foreground">–</span>;
  return (
    <details className="text-xs">
      <summary className="cursor-pointer whitespace-nowrap font-mono">{fmtPrice(row.fill)} · {FILL_LABEL[row.fillSrc]}</summary>
      <div className="mt-2 min-w-72 space-y-2">
        {row.executions.map((e, i) => {
          const time = e.phase === "open" ? "시가" : "종가";
          const buy = e.side === "B";
          const operator = (strategy === "prevClose") === buy ? "<" : ">";
          return <div key={i} className="border-t pt-1.5">
            <div className="font-mono">{time} · {buy ? "매수" : "매도"} {e.quantity}계약 @ {fmtPrice(e.price)} ({FILL_LABEL[e.source]})</div>
            <div className="text-muted-foreground">{e.reason === "tp" ? "전일 이격도 변곡에 따른 익실현" : `${fmtPrice(buy ? e.buyPrice : e.sellPrice, 6)} ${operator} ${row.referenceDate} ${strategy === "prevClose" ? "종가" : "MA"} ${e.reference == null ? "–" : fmtPrice(e.reference, 6)}`}</div>
          </div>;
        })}
        <p className="text-muted-foreground">당일 슬리피지·수수료 합계 {fmtNum(row.cost)}원</p>
      </div>
    </details>
  );
}

function ActionBadge({ r }: { r: Row }) {
  if (!r.action) return <span className="text-xs text-muted-foreground">관망</span>;
  const map = {
    B: ["매수", "text-[var(--bt-up)] border-[var(--bt-up)]/40"],
    S: ["매도", "text-[var(--bt-down)] border-[var(--bt-down)]/40"],
    BS: ["매수·매도", "text-foreground border-border"],
    TP: ["익실현", "text-[var(--bt-tp)] border-[var(--bt-tp)]/40"],
  } as const;
  const [label, cls] = map[r.action];
  return <Badge variant="outline" className={cls}>{label}</Badge>;
}

const CAPS = [1, 5, 0];
const SCENARIOS: { key: string; label: string; params: Partial<Params> }[] = [
  { key: "ma-open", label: "전일 MA 신호 → 다음 시가", params: { strategy: "ma", timing: "prev", buyFill: "open", sellFill: "open" } },
  { key: "ma-close", label: "전일 MA 신호 → 다음 종가", params: { strategy: "ma", timing: "prev", buyFill: "close", sellFill: "close" } },
  { key: "ma-split", label: "전일 MA 신호 → 다음 시가+종가", params: { strategy: "ma", timing: "prev", buyFill: "openClose", sellFill: "openClose" } },
  { key: "ma-observe-open", label: "시가 vs 전일 MA → 당일 종가", params: { strategy: "ma", timing: "open", buyFill: "close", sellFill: "close" } },
  { key: "prev-close-open", label: "전일 vs 전전일 종가 → 다음 시가", params: { strategy: "prevClose", timing: "prev", buyFill: "open", sellFill: "open" } },
  { key: "prev-close-close", label: "전일 vs 전전일 종가 → 다음 종가", params: { strategy: "prevClose", timing: "prev", buyFill: "close", sellFill: "close" } },
  { key: "prev-close-split", label: "전일 vs 전전일 종가 → 다음 시가+종가", params: { strategy: "prevClose", timing: "prev", buyFill: "openClose", sellFill: "openClose" } },
  { key: "observe-open", label: "시가 vs 전일 종가 → 당일 종가", params: { strategy: "prevClose", timing: "open", buyFill: "close", sellFill: "close" } },
];

function PeriodComparison({ base }: { base: UIState }) {
  const results = useMemo(() => {
    const D = DATA[base.product];
    return [
      { label: "선택 기간", start: base.start, end: base.end },
      { label: "올해", start: `${new Date().getFullYear()}-01-01`, end: D.d.at(-1)! },
      { label: "전체 데이터", start: D.d[0], end: D.d.at(-1)! },
    ].map((period) => ({ ...period, stats: run(D, { ...base, ...period }).stats }));
  }, [base]);
  return <Card size="sm">
    <CardHeader><CardTitle>같은 설정 · 기간 비교</CardTitle><CardDescription>{PRODUCT_LABEL[base.product]}의 현재 조건·체결·비용·한도를 유지합니다. 각 기간은 포지션 0에서 시작하며, 이평 준비에는 시작일 이전 데이터도 사용합니다.</CardDescription></CardHeader>
    <CardContent className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>기간</TableHead><TableHead>실제 계산 범위</TableHead><TableHead className="text-right">누적 손익</TableHead><TableHead className="text-right">MDD</TableHead><TableHead className="text-right">총 거래 계약</TableHead></TableRow></TableHeader>
        <TableBody>{results.map(({ label, stats }) => <TableRow key={label}>
          <TableCell>{label}</TableCell><TableCell>{stats ? `${stats.from} ~ ${stats.to}` : "계산 가능한 데이터 없음"}</TableCell>
          <TableCell className={signed(stats?.total ?? 0)}>{stats ? fmtEok(stats.total, 2) : "–"}</TableCell>
          <TableCell className="text-right font-mono">{stats ? fmtEok(stats.mdd, 2) : "–"}</TableCell>
          <TableCell className="text-right font-mono">{stats ? fmtNum(stats.lots) : "–"}</TableCell>
        </TableRow>)}</TableBody>
      </Table>
    </CardContent>
  </Card>;
}

function Compare({ base }: { base: UIState }) {
  const grid = useMemo(() => {
    const out: { product: Product; sc: (typeof SCENARIOS)[number]; cap: number; st: NonNullable<ReturnType<typeof run>["stats"]>; lots: number }[] = [];
    const scenarios = [{ key: "current", label: `현재 설정 · 매수 ${FILL_OPTIONS[base.buyFill]} / 매도 ${FILL_OPTIONS[base.sellFill]}`, params: {} }, ...SCENARIOS];
    for (const product of ["ktb3", "ktb10"] as Product[])
      for (const sc of scenarios)
        for (const cap of CAPS) {
          const r = run(DATA[product], { ...base, ...sc.params, capDays: cap });
          if (r.stats) out.push({ product, sc, cap, st: r.stats, lots: r.lotsPerDay });
        }
    return out;
  }, [base]);
  return (
    <div className="space-y-4">
    <PeriodComparison base={base} />
    <Card size="sm">
      <CardHeader>
        <CardTitle>신호 시점 · 체결가 모델 · 보유 한도 민감도</CardTitle>
        <CardDescription>
          현재 설정의 MA 가격 조건·방향·익실현·비용·기간({base.start} ~ {base.end})을 유지하고 상품·전략·신호/체결 방식·보유 한도를 바꿉니다. 전일 종가 전략에는 MA 가격 조건이 적용되지 않습니다. 두 상품 모두 현재 일일 주문 수량으로 비교합니다. 시가 관측 모드는 선택한 OHLC 조건 대신 당일 시가를 비교합니다.
        </CardDescription>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>상품</TableHead>
              <TableHead>신호 · 체결</TableHead>
              <TableHead className="text-right">최대 보유</TableHead>
              <TableHead className="text-right">누적 손익</TableHead>
              <TableHead className="text-right">연평균</TableHead>
              <TableHead className="text-right">MDD</TableHead>
              <TableHead className="text-right">연손익/MDD</TableHead>
              <TableHead className="text-right">샤프</TableHead>
              <TableHead className="text-right">평균 보유</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {grid.map((g) => (
              <TableRow key={`${g.product}-${g.sc.key}-${g.cap}`}>
                <TableCell>{PRODUCT_LABEL[g.product]} <span className="text-xs text-muted-foreground">({g.lots}/일)</span></TableCell>
                <TableCell>{g.sc.label}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{g.cap ? `${fmtNum(g.cap * g.lots)}계약` : "무제한"}</TableCell>
                <TableCell className={signed(g.st.total)}>{fmtEok(g.st.total)}</TableCell>
                <TableCell className={signed(g.st.perYear)}>{fmtEok(g.st.perYear, 2)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{fmtEok(g.st.mdd)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{fmtRatio(g.st.calmar)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{fmtRatio(g.st.sharpe)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{fmtNum(g.st.avgAbsPos)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
    </div>
  );
}

function Notes() {
  return (
    <Card size="sm">
      <CardContent className="grid gap-6 text-sm leading-relaxed md:grid-cols-2">
        <section className="space-y-2">
          <h2 className="font-semibold">PIT · 정보 관측과 주문 순서</h2>
          <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
            <li><b>전일 확정 신호</b> — 전일 OHLC와 전일 종가 기반 5일선은 전일 장 마감 후 알 수 있습니다. 이 정보로 주문을 결정하고 다음 거래일 시가·종가에 체결합니다. 전일 저가·고가를 신호에 쓰는 것은 가능합니다.</li>
            <li><b>당일 시가 관측 신호</b> — 당일 시가를 확인한 뒤 전일 확정 5일선 또는 전일 종가와 비교합니다. 체결은 이후 당일 종가로 제한하며, 마감 때 조건을 다시 평가하지 않습니다. 이 모드에서는 매수·매도 가격 조건이 모두 당일 시가입니다.</li>
            <li><b>당일 종가 관측 신호</b> — 당일 종가로 판단한 주문은 다음 거래일부터 적용합니다. 확정 종가를 보고 같은 종가에 체결하는 조합은 제공하지 않습니다.</li>
            <li><b>전일 종가 대비 전략</b> — 전일 확정 모드는 전일 종가와 전전 거래일 종가를 비교합니다. 당일 시가 모드는 당일 시가와 전일 종가를 비교합니다. 비교 가격이 기준보다 낮으면 매수, 높으면 매도, 같으면 관망합니다.</li>
            <li><b>분봉 부재</b> — 일봉 고가·저가의 발생 시각과 순서는 알 수 없습니다. 장중 경로·10분 분할·VWAP 체결을 계산하지 않습니다. 과거 버전의 장중 가정 결과는 이 모델과 직접 비교할 수 없습니다.</li>
            <li><b>이평 가격</b> — 5일선은 수정 종가의 단순이평입니다. 전일 확정 모드에서 저가 조건을 선택해도 이평의 원천은 종가입니다. 매수는 초과, 매도는 미만이며 동시 충족은 관망합니다.</li>
          </ul>
        </section>
        <section className="space-y-2">
          <h2 className="font-semibold">수량·데이터·체결 가정</h2>
          <ul className="list-disc space-y-2 pl-5 text-muted-foreground">
            <li><b>하루 수량</b> — 직접 입력한 매수·매도 합계 한도입니다. 시가 또는 종가 일괄은 이 수량을 한 번에, 시가·종가 분할은 절반씩 주문합니다. 홀수의 나머지는 종가에 배정하며 미체결 수량을 이월하지 않습니다.</li>
            <li><b>체결 가정</b> — 사전에 결정한 주문이 시가·종가에 체결된다고 가정합니다. 실제 주문 방식·거래량·호가에 따른 체결 보장은 없으며 슬리피지·수수료는 입력값으로 반영합니다. 두 시점 수량이 같을 때만 평균가가 (시가+종가)/2입니다.</li>
            <li><b>데이터</b> — 국채선물일봉.xlsx의 수정연결 일봉입니다. 월물 교체 갭을 과거 가격에 더하는 차감식 보정이며, 별도 시점별 데이터 빈티지는 없습니다. 일부 원본 OHLC의 범위 불일치 일수는 상단에 표시합니다.</li>
            <li><b>포지션</b> — 롱·숏 또는 롱 온리. 보유 한도는 일일 주문 수량 × 선택 일수이며 롱 온리의 매도는 보유 청산까지만 허용합니다.</li>
            <li><b>익실현</b> — 전일 확정 이격도 변곡으로 청산을 결정하고 선택한 체결 시각과 수량 한도를 따릅니다. 대상 포지션을 모두 청산하면 익실현 상태를 해제합니다.</li>
            <li><b>손익</b> — 기존 보유의 전일 종가 대비 평가손익 + 당일 체결의 종가 평가손익 − 거래량별 비용. 평가에 쓰는 종가는 앞선 주문 판단에 사용하지 않습니다. 종료일 강제청산은 없으며 증거금·이자·별도 롤 비용은 미반영입니다.</li>
            <li><b>기간 비교</b> — 각 기간은 포지션 0에서 독립 계산합니다. 연도별 표는 선택 기간 내 연속 포지션의 연간 손익입니다.</li>
          </ul>
        </section>
      </CardContent>
    </Card>
  );
}
